import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/20/solid'
import { PlusIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { useNotificationContext } from '../../context'
import { getInviteToAssessmentLink } from '../../helpers'
import { useCopyToClipboard } from '../../hooks/use-copy-to-clipboard'
import { AnimatedCopyIcon, SuccessCheck } from '../motion'
import Button from '../tailwind/Button'
import { InviteMeta, MailNotSentIcon } from './invite-success'

const I18N = 'components.career.invite_talent_modal.'

// Eine Adresse mit ihrem Ergebnis. Verschickte haben den Link als Fallback
// zum Kopieren, falls die Mail im Spam landet.
const OutcomeRow = ({ outcome }) => {
  const { t } = useTranslation()
  const { error } = useNotificationContext()
  const linkCopy = useCopyToClipboard()

  const { email, firstname, lastname, space } = outcome
  const name = [firstname, lastname]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ')

  const copyLink = async () => {
    const link = getInviteToAssessmentLink({
      space_id: space.id,
      language: (space.language || '').toLowerCase()
    })

    if (!(await linkCopy.copy(link))) error(t(I18N + 'copy_error'))
  }

  return (
    <li
      data-outcome={space ? 'sent' : 'failed'}
      className='flex items-center gap-x-3 py-2 pl-3 pr-2'
    >
      {space ? (
        <CheckCircleIcon
          className='size-5 shrink-0 text-green-500'
          aria-hidden='true'
        />
      ) : (
        <XCircleIcon
          className='size-5 shrink-0 text-red-500'
          aria-hidden='true'
        />
      )}
      <div className='min-w-0 flex-1'>
        <p className='truncate text-sm font-medium text-gray-900'>{email}</p>
        {name && <p className='truncate text-xs text-gray-500'>{name}</p>}
      </div>
      {space ? (
        <>
          <span className='sr-only'>{t(I18N + 'batch_status_sent')}</span>
          <button
            type='button'
            className='shrink-0 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
            aria-label={t(I18N + 'copy_recipient_link_action', { email })}
            title={t(I18N + 'copy_invite_link_fallback')}
            onClick={copyLink}
          >
            <AnimatedCopyIcon copied={linkCopy.copied} />
          </button>
        </>
      ) : (
        <span className='shrink-0 text-xs font-medium text-red-600'>
          {t(I18N + 'batch_status_failed')}
        </span>
      )}
    </li>
  )
}

OutcomeRow.propTypes = {
  outcome: PropTypes.shape({
    email: PropTypes.string.isRequired,
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    space: PropTypes.shape({
      id: PropTypes.string.isRequired,
      language: PropTypes.string
    }),
    // Talent angelegt, nur die Mail fehlt. Zählt hier wie nicht verschickt.
    unsentSpaceId: PropTypes.string
  }).isRequired
}

// Erfolgs-Step nach dem Einladen mehrerer Adressen. Er zählt, was unterwegs
// ist, listet jede Adresse mit Ergebnis und schickt gescheiterte erneut.
const InviteBatchSuccess = ({
  outcomes,
  careerTitle,
  progress,
  onRetry,
  onInviteAnother,
  onDone
}) => {
  const { t } = useTranslation()

  const sent = outcomes.filter(({ space }) => space)
  const failedCount = outcomes.length - sent.length
  // Sprache und Frist kommen aus Partner und Stelle, sie gelten für alle
  const space = sent[0]?.space || {}

  const title = failedCount
    ? t(I18N + 'success_batch_title_partial', {
        count: sent.length,
        total: outcomes.length
      })
    : t(I18N + 'success_batch_title', { count: sent.length })

  const description = failedCount
    ? t(I18N + 'success_batch_description_partial', { count: failedCount })
    : t(I18N + 'success_batch_description')

  return (
    <div>
      {/* Ohne verschickte Mail kein Haken, auch wenn die Talente stehen */}
      {sent.length ? <SuccessCheck /> : <MailNotSentIcon />}
      <h2 className='mt-4 text-2xl font-extrabold tracking-tight text-gray-900'>
        {title}
      </h2>
      <p className='mt-2 text-sm text-gray-600'>{description}</p>
      <InviteMeta
        careerTitle={careerTitle}
        language={(space.language || '').toLowerCase()}
        expiresAt={space.expiresAt}
      />
      <ul className='mt-5 max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-lg ring-1 ring-inset ring-gray-200'>
        {outcomes.map((outcome) => (
          <OutcomeRow key={outcome.email} outcome={outcome} />
        ))}
      </ul>
      {failedCount > 0 && (
        <div className='mt-3 flex flex-wrap items-center justify-end gap-x-3 gap-y-2'>
          {progress && (
            <p className='text-sm text-gray-500' aria-live='polite'>
              {t(I18N + 'sending_progress', progress)}
            </p>
          )}
          <Button.SecondarySM
            text={t(I18N + 'retry_failed_action', { failed: failedCount })}
            isLoading={!!progress}
            onClick={onRetry}
          />
        </div>
      )}
      <div className='mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-gray-100 pt-5'>
        <Button.Text
          minWidth=''
          icon={<PlusIcon className='size-4' aria-hidden='true' />}
          text={t(I18N + 'invite_more_action')}
          onClick={onInviteAnother}
          disabled={!!progress}
        />
        <Button.SecondaryXL
          autoFocus
          text={t(I18N + 'done_action')}
          onClick={onDone}
          disabled={!!progress}
        />
      </div>
    </div>
  )
}

InviteBatchSuccess.propTypes = {
  outcomes: PropTypes.arrayOf(OutcomeRow.propTypes.outcome).isRequired,
  careerTitle: PropTypes.string,
  // { done, total } während „Erneut senden“ läuft
  progress: PropTypes.shape({
    done: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired
  }),
  onRetry: PropTypes.func.isRequired,
  onInviteAnother: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired
}

InviteBatchSuccess.defaultProps = {
  careerTitle: undefined,
  progress: null
}

export default InviteBatchSuccess
